import {
  EolRelease,
  compareVersions,
} from '@internal/backstage-plugin-framework-versions-common';

export type Release = EolRelease;

export type FrameworkStatus =
  | 'unsupported'
  | 'below-standard'
  | 'nearing-eol'
  | 'supported'
  | 'unknown';

export const STATUS_TEXT: Record<FrameworkStatus, string> = {
  supported: 'Supported',
  'nearing-eol': 'Nearing end of life',
  'below-standard': 'Below isolved standard',
  unsupported: 'End of life',
  unknown: 'Unknown',
};

const DAY_MS = 864e5;

// endoflife.date names cycles differently per product and era (.NET 8 is
// "8" but .NET Core 3.1 is "3.1"), so try the most specific name first.
const findRelease = (version: string, releases: Release[]) => {
  const parts = version.split('.');
  const candidates = [version, parts.slice(0, 2).join('.'), parts[0]];
  for (const cycle of candidates) {
    const release = releases.find(r => r.cycle === cycle);
    if (release) return release;
  }
  return undefined;
};

const WORST_FIRST: FrameworkStatus[] = [
  'unsupported',
  'below-standard',
  'nearing-eol',
  'unknown',
  'supported',
];

export const worstStatus = (statuses: FrameworkStatus[]): FrameworkStatus =>
  WORST_FIRST.find(s => statuses.includes(s)) ?? 'unknown';

export const evaluateStatus = (input: {
  version: string;
  releases?: Release[];
  minimum?: string;
  now: Date;
  nearingDays?: number;
}): { status: FrameworkStatus; eol?: string } => {
  const { version, releases, minimum, now, nearingDays = 180 } = input;
  const release = releases && findRelease(version, releases);
  if (!release) return { status: 'unknown' };

  const { eol } = release;
  const eolTime = eol ? new Date(eol).getTime() : undefined;
  const result = (status: FrameworkStatus) => ({ status, eol });

  if (release.isEol || (eolTime !== undefined && eolTime <= now.getTime())) {
    return result('unsupported');
  }
  if (minimum && compareVersions(version, minimum) < 0) {
    return result('below-standard');
  }
  if (
    eolTime !== undefined &&
    eolTime - now.getTime() <= nearingDays * DAY_MS
  ) {
    return result('nearing-eol');
  }
  return result('supported');
};
